import { useQuery } from '@apollo/client/react';
import { useCookies } from '@fergdigitalcommerce/fergy-react-components';
import loadable from '@loadable/component';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, type ReactElement, useContext } from 'react';
import { configureRoute } from '../../../../utils/routes/configure-route';
import {
	type PlaPageQuery,
	type PlaPageQueryVariables,
	type ProductVariantFieldsFragment
} from '../../../__generated__/graphql-client-types';
import { PageLoading } from '../../../components/common-components/loading/loading.component';
import { FiveHundred } from '../../../components/error-components/500/500.component';
import { ProductNotFound } from '../../../components/product-components/product-not-found/product-not-found.component';
import { ProductPageSkeleton } from '../../../components/product-page-skeleton/product-page-skeleton.component';
import { COOKIE_LAST_PRODUCT } from '../../../constants/cookies';
import { FEATURE_FLAGS } from '../../../constants/feature-flags';
import { PRODUCT_SUMMARY_ROUTE } from '../../../constants/links';
import { DEFAULT_PLA_PRODUCT_STATUS } from '../../../constants/product';
import { ProductFamilyContextProvider } from '../../../contexts/product-family/product-family.context';
import { VariantContext, VariantContextProvider } from '../../../contexts/variant/variant.context';
import { ANALYTICS_PAGE_NAMES } from '../../../helpers/analytics/analytics.helper';
import { findVariant, isProductNotFound } from '../../../helpers/product-helper/product.helper';
import { useTrackPageView } from '../../../hooks/analytics/analytics.hooks';
import { useDyRecommendationContext, DyRecommendationContextType } from '../../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useFeature } from '../../../hooks/features/features.hooks';
import { useCustomerZipCodeWatcher } from '../../../hooks/general/location-watcher.hooks';
import { useUpdateUrlOnVariantChange } from '../../../hooks/product/product.hooks';
import { useRouterLocation } from '../../../hooks/routing/location.hook';
import { useRouteParams } from '../../../hooks/routing/params.hook';
import { PLA } from '../../../queries/product/pla/pla.queries';
import { type PlaProductFamily } from '../../../types/product.types';
import { ProductSummaryPage } from '../pla-summary/pla-summary.page';

// dynamically import discontinued product page
const LoadableDiscontinuedProduct = loadable(
	() => import(/* webpackChunkName: "discontinuedproduct" */ '../../product/discontinued-product/discontinued-product.page'),
	{
		resolveComponent: ({ DiscontinuedProduct }) => DiscontinuedProduct
	}
);

function generateDataLayer(product: PlaProductFamily, selectedVariant: ProductVariantFieldsFragment) {
	return {
		selectedFinish: {
			priceValue: selectedVariant?.priceInfo?.current,
			uniqueId: selectedVariant?.id,
			title: selectedVariant?.title,
			sale: selectedVariant?.sales,
			modelNumber: selectedVariant?.modelNumber,
			shippingClassifications: selectedVariant?.shippingClassifications,
			isOutOfStock: selectedVariant?.isOutOfStock
		},
		manufacturer: product?.brandInfo?.name,
		product: {
			businessCategory: product?.businessCategory?.name,
			baseCategory: product?.baseCategory?.name,
			application: product?.application,
			type: product?.type,
			title: product?.title
		},
		collection: product?.collection?.name || '',
		page: `${ANALYTICS_PAGE_NAMES.PRODUCT_LISTING_AD}:display`
	};
}

type DynamicYieldProviderProps = {
	children: ReactElement;
	variantId: number;
};

const DynamicYieldProvider: FunctionComponent<DynamicYieldProviderProps> = ({ variantId, children }) => {
	useDyRecommendationContext({
		type: DyRecommendationContextType.PRODUCT_PAGE,
		data: [`${variantId}`]
	});
	return children;
};

type AnalyticsProviderProps = {
	children: ReactElement;
	product: PlaProductFamily;
	isLoading: boolean;
	isDiscontinued: boolean;
};

/**
 * Component that is solely responsible for handling page level analytics
 */
const AnalyticsProvider: FunctionComponent<AnalyticsProviderProps> = ({ children, product, isLoading, isDiscontinued }) => {
	const { isSelectedVariantConfirmed, selectedVariant } = useContext(VariantContext);

	useTrackPageView(
		{
			pageName: 'product:summary',
			businessCategory: product?.businessCategory?.name,
			baseCategory: product?.baseCategory?.name,
			brand: product?.brandInfo?.name,
			collection: product?.collection?.name || ''
		},
		generateDataLayer(product, selectedVariant),
		isLoading || selectedVariant === undefined || (!isSelectedVariantConfirmed && !isDiscontinued)
	);

	return children;
};
export const PlaRouter: FunctionComponent = () => {
	const skeletonLoaderActive = useFeature(FEATURE_FLAGS.PRODUCT_PAGE_SKELETON_LOADER, { isSiteWide: true });
	const updateUrl = useUpdateUrlOnVariantChange();
	const { familyId = '0' } = useRouteParams<{ familyId: string }>();
	const { pathname, search } = useRouterLocation();
	const { setCookie } = useCookies();
	const familyIdNumber = Number(familyId);
	/**
	 * We set this last product cookie to be compliant with other pages that
	 * use this data (including pages on node-store)
	 */
	setCookie(COOKIE_LAST_PRODUCT, `${pathname}${search}`);
	const searchParams = new URLSearchParams(search);
	const uid = Number(searchParams.get('uid') || '0');
	const regionId = searchParams.get('region_id') || undefined;

	const {
		data: { productFamily: product } = {},
		loading,
		error,
		refetch: refetchPLA
	} = useQuery<PlaPageQuery, PlaPageQueryVariables>(PLA, {
		variables: { id: familyIdNumber, productStatus: DEFAULT_PLA_PRODUCT_STATUS, regionId }
	});

	// Variant shipping info depends on customer zip - so refetch if zip changes
	useCustomerZipCodeWatcher(() => {
		void refetchPLA();
	});

	// IMPROVEMENT: Generic errors should be handled by the PageContainer.
	if (error) {
		return <FiveHundred />;
	}

	if (loading) {
		return skeletonLoaderActive ? <ProductPageSkeleton /> : <PageLoading />;
	}

	if (!product || isProductNotFound(product)) {
		return <ProductNotFound />;
	}

	// make the default variant selected
	const productUid = Number(uid) || product?.variants?.[0]?.id || 0;
	const initialVariant = findVariant(product, productUid, ['stock', 'nonstock', 'discontinued']);

	// this should not happen but it might be possible for findVariant to fail to come up with an initial variant
	if (!initialVariant) {
		logError(new Error(`could not find variant for: ${product.id} with uiqueId ${uid}`));
		return <ProductNotFound />;
	}

	return (
		<ProductFamilyContextProvider {...product}>
			<VariantContextProvider onSelection={updateUrl} {...initialVariant}>
				<AnalyticsProvider product={product} isLoading={loading} isDiscontinued={product.isDiscontinued}>
					<DynamicYieldProvider variantId={initialVariant ? initialVariant.id : productUid}>
						{product.isDiscontinued ? (
							<LoadableDiscontinuedProduct productUid={productUid} productFamilyId={familyIdNumber} pageName="pla" />
						) : (
							<ProductSummaryPage product={product} productUid={productUid} regionId={regionId} />
						)}
					</DynamicYieldProvider>
				</AnalyticsProvider>
			</VariantContextProvider>
		</ProductFamilyContextProvider>
	);
};

export const RoutedPlaPage = configureRoute(
	{
		path: PRODUCT_SUMMARY_ROUTE,
		ddRum: { rumViewName: 'PLA' }
	},
	PlaRouter
);
